import { OFFICER_STATUS } from './enums';

/** Reserved FS case status `name` for Closed/Completed — only changeable from the case page. */
const CLOSED_CASE_STATUS_NAME = 'completed';

/** Reserved FS case status `name` for Working (label and color are editable, the name is not). */
const WORKING_STATUS_NAME = 'working';

/** Two series colors checked for color-blind separation; the backlog line is a neutral ink. */
const MANAGEMENT_CHART_COLORS = {
  BACKLOG: '#52514e',
  CLOSED: '#eb6834',
  GRID: '#e5e7eb',
  OPENED: '#2a78d6',
};

/** None is red (the overdue Check-In) or green (In Service). `pinColor` is the badge color as hex, for the map pins. */
const DISPATCHER_CLOCK_STEPS = {
  caseWork: {
    badgeClass: 'bg-teal-500',
    pinColor: '#14B8A6',
    icon: 'pi pi-briefcase',
    labelKey: 'fieldServices.dashboard.workingCase.caseWork',
    tooltipKey: 'fieldServices.dashboard.workingCase.caseWorkTime',
  },
  enRoute: {
    badgeClass: 'bg-blue-500',
    pinColor: '#3B82F6',
    icon: 'pi pi-car',
    labelKey: 'fieldServices.dashboard.workingCase.enRoute',
    tooltipKey: 'fieldServices.dashboard.workingCase.enRouteTime',
  },
  onScene: {
    badgeClass: 'bg-purple-500',
    pinColor: '#A855F7',
    icon: 'pi pi-map-marker',
    labelKey: 'fieldServices.dashboard.workingCase.onSite',
    tooltipKey: 'fieldServices.dashboard.workingCase.onSiteTime',
  },
  transporting: {
    badgeClass: 'bg-orange-500',
    pinColor: '#F97316',
    icon: 'pi pi-truck',
    labelKey: 'fieldServices.dashboard.workingCase.transporting',
    tooltipKey: 'fieldServices.dashboard.workingCase.transportingTime',
  },
};

/** The dispatch settings' Check-In step, used until the shelter saves its own. */
const DEFAULT_INCREMENT_MINUTES = 10;

const DISPATCHER_WORKING_BADGE = {
  badgeClass: 'bg-purple-500',
  pinColor: '#A855F7',
  icon: 'pi pi-stopwatch',
  labelKey: 'fieldServices.dashboard.map.legend.working',
};

const OFFICER_STATUS_BADGES: Record<OFFICER_STATUS, { badgeClass: string; icon: string }> = {
  [OFFICER_STATUS.ONLINE]: { badgeClass: '!bg-green-500', icon: 'pi pi-check-circle' },
  [OFFICER_STATUS.OUT_OF_SERVICE]: { badgeClass: '!bg-slate-600', icon: 'pi pi-ban' },
  [OFFICER_STATUS.OFFLINE]: { badgeClass: '!bg-gray-400', icon: 'pi pi-moon' },
};

const OFFICER_STATUS_LABEL_KEYS: Record<OFFICER_STATUS, string> = {
  [OFFICER_STATUS.ONLINE]: 'fieldServices.dashboard.officerStatus.online',
  [OFFICER_STATUS.OUT_OF_SERVICE]: 'fieldServices.dashboard.officerStatus.outOfService',
  [OFFICER_STATUS.OFFLINE]: 'fieldServices.dashboard.officerStatus.offline',
};

const DISPATCHER_ADDRESS_PART_FIELDS = ['city', 'state', 'zip'];

const DISPATCHER_COMPACT_MERGED_FIELDS = [
  'address',
  'animals',
  'call_date_formatted',
  'call_type',
  'complaint',
  'id',
  'jurisdiction',
  'los',
  'priority',
  'reporting_party_name',
  'status',
  'working_seconds',
];

const OFFICER_TABLE_EXCLUDE_FIELDS = ['assigned_full_name'];

const OFFICER_PAGE_EXCLUDE_FIELDS = [...OFFICER_TABLE_EXCLUDE_FIELDS, 'source'];

/** The two roles the api lists as Field Services officers (`UserRoleRepository::getFieldServiceOfficers`). */
const FIELD_SERVICES_OFFICER_ROLES = ['field-services-manager', 'field-services-officer'];

const SCHEDULE_DEFAULT_DURATION_MINUTES = 30;

/** Overdue is not a stored status (the api works it out on read), so it has its own key. */
const SCHEDULE_STATUS_COLORS: Record<string, string> = {
  canceled: '#9ca3af',
  completed: '#16a34a',
  overdue: '#dc2626',
  scheduled: '#2563eb',
};

const SECONDS_PER_HOUR = 3600;
const SECONDS_PER_MINUTE = 60;

// Shared by the Field Services report screens. Case Stats and the Map deliberately keep
// MDatePicker's own 365-day default and must NOT import this.
const REPORT_MAX_RANGE_DAYS = 1825;

// The api gives a report 60s before it gives up (QUERY_MAX_TIME_MS). Axios defaults to 30s, so
// without this the browser quit first and a 30-60s report could never succeed - and the api's own
// "too many rows" refusal never reached the user. Sit just above the server ceiling so the server
// is always the one that decides.
const REPORT_REQUEST_TIMEOUT_MS = 65000;

export {
  CLOSED_CASE_STATUS_NAME,
  DEFAULT_INCREMENT_MINUTES,
  DISPATCHER_ADDRESS_PART_FIELDS,
  DISPATCHER_CLOCK_STEPS,
  DISPATCHER_COMPACT_MERGED_FIELDS,
  DISPATCHER_WORKING_BADGE,
  FIELD_SERVICES_OFFICER_ROLES,
  MANAGEMENT_CHART_COLORS,
  OFFICER_STATUS_BADGES,
  OFFICER_PAGE_EXCLUDE_FIELDS,
  OFFICER_STATUS_LABEL_KEYS,
  OFFICER_TABLE_EXCLUDE_FIELDS,
  REPORT_MAX_RANGE_DAYS,
  REPORT_REQUEST_TIMEOUT_MS,
  SCHEDULE_DEFAULT_DURATION_MINUTES,
  SCHEDULE_STATUS_COLORS,
  SECONDS_PER_HOUR,
  SECONDS_PER_MINUTE,
  WORKING_STATUS_NAME,
};
